import React from "react";
import { Box } from "@mui/material";
import { certificatePaperColour } from "../../../domain/certificates/certificateTemplates";
import { CentredLayout, type CertificateDesign } from "./certificateParts";

/** Where the gap between the thick and thin borders runs, in from the page edge. */
const RULE_GAP = 32;
const DIAMOND = 30;
const ROSETTE = { width: 120, height: 170 };
/** On a portrait page the rosette sits above the text, which starts just below it. */
const PORTRAIT_ROSETTE_TOP = 48;

/** Where an ornament sits on the page, in CSS. */
type Placement = Pick<
  React.CSSProperties,
  "top" | "right" | "bottom" | "left" | "transform"
>;

/** The middle of each edge, on the gap between the borders. */
const DIAMOND_PLACEMENTS: Placement[] = [
  { top: RULE_GAP, left: "50%" },
  { bottom: RULE_GAP, left: "50%" },
  { left: RULE_GAP, top: "50%" },
  { right: RULE_GAP, top: "50%" },
].map((edge) => ({
  ...edge,
  // Centre the diamond on that point, whichever sides it's measured from.
  transform: `translate(${"right" in edge ? "50%" : "-50%"}, ${"bottom" in edge ? "50%" : "-50%"})`,
}));

/** A diamond sat across both borders, mid-edge, hiding them where it sits. */
const Diamond: React.FC<{
  primary: string;
  accent: string;
  paper: string;
  placement: Placement;
}> = ({ primary, accent, paper, placement }) => (
  <Box
    component="svg"
    viewBox="0 0 30 30"
    aria-hidden="true"
    sx={{ position: "absolute", width: DIAMOND, height: DIAMOND, ...placement }}
  >
    <polygon
      points="15,1 29,15 15,29 1,15"
      fill={paper}
      stroke={primary}
      strokeWidth={2}
    />
    <polygon points="15,8 22,15 15,22 8,15" fill={accent} />
  </Box>
);

/** Points of a five-pointed star centred on (cx, cy). */
function starPoints(cx: number, cy: number, outer: number, inner: number) {
  return Array.from({ length: 10 }, (_, i) => {
    const radius = i % 2 === 0 ? outer : inner;
    const angle = (Math.PI / 5) * i - Math.PI / 2;
    return `${cx + radius * Math.cos(angle)},${cy + radius * Math.sin(angle)}`;
  }).join(" ");
}

/** A ribbon rosette: a scalloped disc with two tails, no text. */
const Rosette: React.FC<{
  primary: string;
  accent: string;
  placement: Placement;
}> = ({ primary, accent, placement }) => (
  <Box
    component="svg"
    viewBox="0 0 120 170"
    aria-hidden="true"
    sx={{ position: "absolute", ...ROSETTE, ...placement }}
  >
    <polygon points="42,78 20,160 34,150 45,166 62,88" fill={primary} />
    <polygon points="78,78 100,160 86,150 75,166 58,88" fill={primary} />
    {Array.from({ length: 18 }, (_, i) => {
      const angle = ((Math.PI * 2) / 18) * i;
      return (
        <circle
          key={i}
          cx={60 + 44 * Math.cos(angle)}
          cy={60 + 44 * Math.sin(angle)}
          r={10}
          fill={accent}
        />
      );
    })}
    <circle cx={60} cy={60} r={46} fill={accent} />
    <circle cx={60} cy={60} r={36} fill={primary} />
    <circle
      cx={60}
      cy={60}
      r={30}
      fill="none"
      stroke={accent}
      strokeWidth={1.5}
    />
    <polygon points={starPoints(60, 60, 18, 7.5)} fill={accent} />
  </Box>
);

/**
 * Classic: a thick-and-thin double border with diamonds mid-edge and a ribbon
 * rosette, like an Olympiad certificate. The rosette sits in the top-left
 * corner on a landscape page and above the text on a portrait one.
 */
export const classicDesign: CertificateDesign = {
  bodyFont: "lora",
  Layout: ({ template, text, orientation }) => {
    const { primaryColour: primary, accentColour: accent } = template;
    const paper = certificatePaperColour(template);
    return (
      <CentredLayout
        template={template}
        text={text}
        orientation={orientation}
        inset={{
          landscape: "64px 160px 56px",
          portrait: `${PORTRAIT_ROSETTE_TOP + ROSETTE.height + 2}px 72px 64px`,
        }}
        frame={
          <>
            <Box
              sx={{
                position: "absolute",
                inset: 18,
                border: `10px solid ${primary}`,
              }}
            />
            <Box
              sx={{
                position: "absolute",
                inset: 36,
                border: `2px solid ${primary}`,
              }}
            />
            {DIAMOND_PLACEMENTS.map((placement, i) => (
              <Diamond
                key={i}
                primary={primary}
                accent={accent}
                paper={paper}
                placement={placement}
              />
            ))}
            <Rosette
              primary={primary}
              accent={accent}
              placement={
                orientation === "landscape"
                  ? { top: 40, left: 40 }
                  : {
                      top: PORTRAIT_ROSETTE_TOP,
                      left: "50%",
                      transform: "translateX(-50%)",
                    }
              }
            />
          </>
        }
      />
    );
  },
};
